import { z } from 'zod';

const CMSFieldTypeSchema = z.enum([
    'text',
    'number',
    'boolean',
    'date',
    'timestamp',
    'url',
    'asset',
    'select',
    'group',
]);
const CMSStatusSchema = z.enum(['active', 'deleted']);
const CMSSortDirectionSchema = z.enum(['asc', 'desc']);
const CMSDefaultSortSchema = z.object({
    fieldId: z.string().min(1),
    direction: CMSSortDirectionSchema,
});
const TimestampLikeSchema = z.unknown();
const CMSSelectOptionSchema = z.object({
    id: z.string().min(1),
    value: z.string().min(1),
    label: z.string().min(1),
    status: CMSStatusSchema,
    createdAt: TimestampLikeSchema,
    updatedAt: TimestampLikeSchema,
    deletedAt: TimestampLikeSchema.nullish(),
});
/**
 * A `group` field's sub-field. Identical to a collection field but without its
 * own `fields` (groups nest exactly one level) — enforced structurally.
 */
const CMSCollectionSubFieldSchema = z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    label: z.string().min(1),
    type: CMSFieldTypeSchema,
    required: z.boolean(),
    repeated: z.boolean().nullish(),
    status: CMSStatusSchema,
    options: z.array(CMSSelectOptionSchema).nullish(),
    createdAt: TimestampLikeSchema,
    updatedAt: TimestampLikeSchema,
    deletedAt: TimestampLikeSchema.nullish(),
});
const CMSCollectionFieldSchema = z.object({
    id: z.string().min(1),
    slug: z.string().min(1),
    label: z.string().min(1),
    type: CMSFieldTypeSchema,
    required: z.boolean(),
    repeated: z.boolean().nullish(),
    status: CMSStatusSchema,
    options: z.array(CMSSelectOptionSchema).nullish(),
    // Sub-fields for `group` fields. Sub-fields are scalar (validated to be
    // non-`group`), so groups nest exactly one level.
    fields: z.array(CMSCollectionSubFieldSchema).nullish(),
    createdAt: TimestampLikeSchema,
    updatedAt: TimestampLikeSchema,
    deletedAt: TimestampLikeSchema.nullish(),
});
const CMSCollectionSyncStatusSchema = z.enum([
    'pending',
    'syncing',
    'synced',
    'failed',
    'disconnected',
]);
const CMSCollectionSourceSchema = z.object({
    type: z.literal('shopify'),
    connectedAccountId: z.string().min(1),
    shopDomain: z.string().min(1),
    shopifyCollectionIds: z.array(z.string().min(1)),
    syncStatus: CMSCollectionSyncStatusSchema,
    syncedAt: TimestampLikeSchema.nullish(),
    lastSyncJobId: z.string().nullish(),
    lastSyncError: z.string().nullish(),
});
const CMSCollectionSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullish(),
    fields: z.array(CMSCollectionFieldSchema),
    displayFieldId: z.string().nullish(),
    defaultSort: CMSDefaultSortSchema.nullish(),
    status: CMSStatusSchema,
    createdAt: TimestampLikeSchema,
    updatedAt: TimestampLikeSchema,
    deletedAt: TimestampLikeSchema.nullish(),
    createdBy: z.string().min(1),
    updatedBy: z.string().min(1),
    source: CMSCollectionSourceSchema.nullish(),
});
const DateOnlySchema = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD date format');
const TimestampStringSchema = z.string().datetime({ offset: true });
const UrlValueSchema = z.string().url();
const CMSAssetReferenceSchema = z.object({
    assetId: z.string().min(1),
    url: z.string().url(),
    type: z.enum(['image', 'video']),
    source: z.enum(['pexels', 'unsplash', 'storage', 'vimeo']).nullish(),
    name: z.string().nullish(),
    alt: z.string().nullish(),
    thumbnail: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
});
const CMSEntryValueSchema = z.union([
    z.string(),
    z.number(),
    z.boolean(),
    CMSAssetReferenceSchema,
    z.null(),
]);
/** A single `group` object value: a map of scalar sub-field values. */
const CMSEntryObjectValueSchema = z.record(z.string(), CMSEntryValueSchema);
/**
 * Stored CMS field value: a scalar for normal fields, an ordered array of
 * scalars for `repeated` scalar fields, an object for `group` fields, or an
 * array of objects for `repeated` group fields.
 */
const CMSEntryStoredValueSchema = z.union([
    CMSEntryValueSchema,
    z.array(CMSEntryValueSchema),
    CMSEntryObjectValueSchema,
    z.array(CMSEntryObjectValueSchema),
]);
const CMSEntrySchema = z.object({
    id: z.string().min(1),
    collectionId: z.string().min(1),
    values: z.record(z.string(), CMSEntryStoredValueSchema),
    status: CMSStatusSchema,
    createdAt: TimestampLikeSchema,
    updatedAt: TimestampLikeSchema,
    deletedAt: TimestampLikeSchema.nullish(),
    createdBy: z.string().min(1),
    updatedBy: z.string().min(1),
    writeSource: z.string().nullish(),
});
const CMSCreateCollectionInputSchema = z.object({
    name: z.string().trim().min(1),
    description: z.string().nullish(),
    fields: z.array(CMSCollectionFieldSchema).min(1),
    displayFieldId: z.string().nullish(),
    defaultSort: CMSDefaultSortSchema.nullish(),
});
const CMSUpdateCollectionInputSchema = z.object({
    name: z.string().trim().min(1).nullish(),
    description: z.string().nullish(),
    fields: z.array(CMSCollectionFieldSchema).nullish(),
    displayFieldId: z.string().nullish(),
    defaultSort: CMSDefaultSortSchema.nullish(),
    status: CMSStatusSchema.nullish(),
});
const CMSCreateEntryInputSchema = z.object({
    values: z.record(z.string(), CMSEntryStoredValueSchema),
});
const CMSUpdateEntryInputSchema = z.object({
    values: z.record(z.string(), CMSEntryStoredValueSchema).nullish(),
    status: CMSStatusSchema.nullish(),
});
const CMSCollectionAttachmentSnapshotSchema = z.object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().nullish(),
    displayFieldId: z.string().nullish(),
    fields: z.array(z.object({
        id: z.string().min(1),
        slug: z.string().nullish(),
        label: z.string().min(1),
        type: CMSFieldTypeSchema,
        required: z.boolean(),
        repeated: z.boolean().nullish(),
        status: CMSStatusSchema,
        fields: z
            .array(z.object({
            id: z.string().min(1),
            slug: z.string().nullish(),
            label: z.string().min(1),
            type: CMSFieldTypeSchema,
            required: z.boolean(),
        }))
            .nullish(),
        options: z
            .array(z.object({
            id: z.string().min(1),
            value: z.string().min(1),
            label: z.string().min(1),
            status: CMSStatusSchema,
        }))
            .nullish(),
    })),
    activeFieldCount: z.number().int().nonnegative(),
});
const CMSEntryAttachmentSnapshotSchema = z.object({
    id: z.string().min(1),
    collectionId: z.string().min(1),
    collectionName: z.string().min(1),
    displayLabel: z.string().min(1),
    values: z.record(z.string(), CMSEntryStoredValueSchema),
    fields: z.array(z.object({
        id: z.string().min(1),
        label: z.string().min(1),
        type: CMSFieldTypeSchema,
        value: CMSEntryStoredValueSchema,
        optionLabel: z.string().nullish(),
    })),
});
const CMSCollectionAttachmentSchema = z.object({
    id: z.string().min(1),
    type: z.literal('cmsCollection'),
    name: z.string().min(1),
    accountId: z.string().min(1),
    collectionId: z.string().min(1),
    snapshot: CMSCollectionAttachmentSnapshotSchema,
});
const CMSEntryAttachmentSchema = z.object({
    id: z.string().min(1),
    type: z.literal('cmsEntry'),
    name: z.string().min(1),
    accountId: z.string().min(1),
    collectionId: z.string().min(1),
    entryId: z.string().min(1),
    snapshot: CMSEntryAttachmentSnapshotSchema,
});
const CMSListCollectionsToolInputSchema = z.object({
    includeDeleted: z.boolean().nullish(),
});
const CMSReadCollectionSchemaToolInputSchema = z.object({
    collectionId: z.string().min(1),
});
const CMSPageEntriesToolInputSchema = z.object({
    collectionId: z.string().min(1),
    offset: z.number().int().min(0).nullish(),
    limit: z.number().int().min(1).max(50).nullish(),
    sortBy: CMSDefaultSortSchema.nullish(),
});
const CMSCollectionFieldDraftToolInputSchema = z.object({
    id: z.string().min(1).nullish(),
    label: z.string().trim().min(1),
    type: CMSFieldTypeSchema,
    required: z.boolean().nullish(),
    repeated: z.boolean().nullish(),
    options: z
        .array(z.object({
        label: z.string().trim().min(1),
        value: z.string().trim().min(1).nullish(),
    }))
        .nullish(),
    // Sub-fields for a `group` field. Each sub-field is a scalar (non-group)
    // field; nested groups are not supported.
    fields: z
        .array(z.object({
        id: z.string().min(1).nullish(),
        label: z.string().trim().min(1),
        type: CMSFieldTypeSchema,
        required: z.boolean().nullish(),
        options: z
            .array(z.object({
            label: z.string().trim().min(1),
            value: z.string().trim().min(1).nullish(),
        }))
            .nullish(),
    }))
        .nullish(),
});
const CMSCreateCollectionToolInputSchema = z.object({
    name: z.string().trim().min(1),
    description: z.string().trim().nullish(),
    fields: z.array(CMSCollectionFieldDraftToolInputSchema).min(1).max(50),
    displayFieldId: z.string().min(1).nullish(),
    defaultSort: CMSDefaultSortSchema.nullish(),
    reason: z.string().trim().min(1),
});
const CMSUpdateCollectionFieldEditToolInputSchema = z.object({
    fieldId: z.string().min(1),
    label: z.string().trim().min(1).nullish(),
    required: z.boolean().nullish(),
    // Append new select options to an existing select field. Existing options
    // are preserved; field ids and types are immutable.
    addOptions: z
        .array(z.object({
        label: z.string().trim().min(1),
        value: z.string().trim().min(1).nullish(),
    }))
        .nullish(),
});
const CMSUpdateCollectionToolInputSchema = z.object({
    collectionId: z.string().min(1),
    name: z.string().trim().min(1).nullish(),
    description: z.string().trim().nullish(),
    addFields: z.array(CMSCollectionFieldDraftToolInputSchema).max(50).nullish(),
    updateFields: z
        .array(CMSUpdateCollectionFieldEditToolInputSchema)
        .max(50)
        .nullish(),
    removeFieldIds: z.array(z.string().min(1)).max(50).nullish(),
    displayFieldId: z.string().min(1).nullish(),
    defaultSort: CMSDefaultSortSchema.nullish(),
    reason: z.string().trim().min(1),
});
const CMSAddEntriesToolInputSchema = z.object({
    collectionId: z.string().min(1),
    entries: z
        .array(z.object({
        values: z.record(z.string(), CMSEntryStoredValueSchema),
    }))
        .min(1)
        .max(50),
    reason: z.string().trim().min(1),
});
const CMSEditEntryToolInputSchema = z.object({
    collectionId: z.string().min(1),
    entryId: z.string().min(1),
    values: z.record(z.string(), CMSEntryStoredValueSchema),
    reason: z.string().trim().min(1),
});
const CMSDeleteEntryToolInputSchema = z.object({
    collectionId: z.string().min(1),
    entryId: z.string().min(1),
    reason: z.string().trim().min(1),
});
const CMSDeleteCollectionToolInputSchema = z.object({
    collectionId: z.string().min(1),
    reason: z.string().trim().min(1),
});
const CMSDuplicateCollectionToolInputSchema = z.object({
    collectionId: z.string().min(1),
    includeItems: z.boolean(),
    reason: z.string().trim().min(1),
});
/**
 * Validate a single scalar value against its field schema. Returns a short
 * reason (without the field label) when invalid, or null when valid.
 */
function validateCMSScalarValue(field, value) {
    const valid = (() => {
        switch (field.type) {
            case 'text':
            case 'select':
                return typeof value === 'string';
            case 'number':
                return typeof value === 'number' && Number.isFinite(value);
            case 'boolean':
                return typeof value === 'boolean';
            case 'date':
                return DateOnlySchema.safeParse(value).success;
            case 'timestamp':
                return TimestampStringSchema.safeParse(value).success;
            case 'url':
                return UrlValueSchema.safeParse(value).success;
            case 'asset':
                return CMSAssetReferenceSchema.safeParse(value).success;
        }
    })();
    if (!valid) {
        return `has an invalid ${field.type} value`;
    }
    if (field.type === 'select' && typeof value === 'string') {
        const hasOption = field.options?.some((option) => option.status === 'active' && option.value === value);
        if (!hasOption) {
            return 'must use an active option';
        }
    }
    return null;
}
function validateCMSEntryValues(fields, values) {
    const activeFields = fields.filter((field) => field.status === 'active');
    const issues = [];
    for (const field of activeFields) {
        const value = values[field.id];
        if (field.type === 'group') {
            const subFields = (field.fields ?? []).filter((sub) => sub.status === 'active');
            const validateObject = (object, prefix) => {
                if (object === null ||
                    typeof object !== 'object' ||
                    Array.isArray(object)) {
                    issues.push(`${prefix} must be an object`);
                    return;
                }
                const record = object;
                for (const sub of subFields) {
                    const subValue = record[sub.id];
                    const isEmpty = subValue === null || subValue === undefined || subValue === '';
                    if (sub.required && isEmpty) {
                        issues.push(`${prefix} ${sub.label} is required`);
                        continue;
                    }
                    if (isEmpty) {
                        continue;
                    }
                    if (Array.isArray(subValue)) {
                        issues.push(`${prefix} ${sub.label} expects a single value`);
                        continue;
                    }
                    const reason = validateCMSScalarValue(sub, subValue);
                    if (reason) {
                        issues.push(`${prefix} ${sub.label} ${reason}`);
                    }
                }
            };
            if (field.repeated) {
                if (value != null && !Array.isArray(value)) {
                    issues.push(`${field.label} must be a list of ${field.label} items`);
                    continue;
                }
                const items = Array.isArray(value) ? value : [];
                if (field.required && items.length === 0) {
                    issues.push(`${field.label} is required`);
                    continue;
                }
                items.forEach((item, index) => {
                    validateObject(item, `${field.label} item ${index + 1}`);
                });
                continue;
            }
            const isEmpty = value === null || value === undefined;
            if (field.required && isEmpty) {
                issues.push(`${field.label} is required`);
                continue;
            }
            if (isEmpty) {
                continue;
            }
            if (Array.isArray(value)) {
                issues.push(`${field.label} expects a single value`);
                continue;
            }
            validateObject(value, field.label);
            continue;
        }
        if (field.repeated) {
            if (value != null && !Array.isArray(value)) {
                issues.push(`${field.label} must be a list of ${field.type} values`);
                continue;
            }
            const items = Array.isArray(value) ? value : [];
            if (field.required && items.length === 0) {
                issues.push(`${field.label} is required`);
                continue;
            }
            items.forEach((item, index) => {
                const reason = validateCMSScalarValue(field, item);
                if (reason) {
                    issues.push(`${field.label} item ${index + 1} ${reason}`);
                }
            });
            continue;
        }
        const isEmpty = value === null || value === undefined || value === '';
        if (field.required && isEmpty) {
            issues.push(`${field.label} is required`);
            continue;
        }
        if (isEmpty) {
            continue;
        }
        if (Array.isArray(value)) {
            issues.push(`${field.label} expects a single value`);
            continue;
        }
        const reason = validateCMSScalarValue(field, value);
        if (reason) {
            issues.push(`${field.label} ${reason}`);
        }
    }
    return {
        success: issues.length === 0,
        issues,
    };
}
// ============================================================================
// CMS render-data contract (cache layer)
// ============================================================================
const CMSRenderedAssetSchema = z.object({
    url: z.string().url(),
    type: z.enum(['image', 'video']).nullish(),
    alt: z.string().nullish(),
    thumbnail: z.string().nullish(),
    width: z.number().nullish(),
    height: z.number().nullish(),
});
const CMSRenderedFieldValueBaseSchema = z.object({
    type: CMSFieldTypeSchema,
    value: z.union([z.string(), z.number(), z.boolean(), z.null()]),
    label: z.string().nullish(),
    asset: CMSRenderedAssetSchema.nullish(),
});
const CMSRenderedFieldValueSchema = CMSRenderedFieldValueBaseSchema.extend({
    items: z.array(CMSRenderedFieldValueBaseSchema).nullish(),
    objectItems: z
        .array(z.record(z.string(), CMSRenderedFieldValueBaseSchema))
        .nullish(),
});
const CachedCMSEntryDataSchema = z.object({
    id: z.string().min(1),
    fields: z.record(z.string(), CMSRenderedFieldValueSchema),
});
const CachedCMSCollectionDataSchema = z.object({
    collectionId: z.string().min(1),
    updatedOn: z.string(),
    entries: z.array(CachedCMSEntryDataSchema),
});
const CMSFilterOpSchema = z.enum([
    'eq',
    'neq',
    'in',
    'is',
    'lt',
    'gt',
    'lte',
    'gte',
    'between',
    'contains',
]);
const CMSFilterClauseSchema = z.object({
    fieldId: z.string().min(1),
    op: CMSFilterOpSchema,
    value: z.union([CMSEntryValueSchema, z.array(CMSEntryValueSchema)]),
});
const CMSListQuerySchema = z.object({
    sort: CMSDefaultSortSchema.nullish(),
    limit: z.number().int().positive().nullish(),
    filter: z.array(CMSFilterClauseSchema).nullish(),
});
// ============================================================================
// CMS render resolution (schema-dependent formatting)
// ============================================================================
const isCMSAssetReference = (value) => Boolean(value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    typeof value.assetId === 'string');
/**
 * Resolve a single raw scalar entry value into normalized render data, using
 * the field's schema (select options, asset normalization, type coercion).
 */
function formatCMSScalarValue(field, raw) {
    const value = Array.isArray(raw) ? null : (raw ?? null);
    switch (field.type) {
        case 'asset': {
            if (isCMSAssetReference(value)) {
                return {
                    type: 'asset',
                    value: value.url,
                    asset: {
                        url: value.url,
                        type: value.type,
                        alt: value.alt ?? null,
                        thumbnail: value.thumbnail ?? null,
                        width: value.width ?? null,
                        height: value.height ?? null,
                    },
                };
            }
            return { type: 'asset', value: null, asset: null };
        }
        case 'select': {
            if (typeof value === 'string') {
                const label = field.options?.find((option) => option.value === value)?.label ??
                    value;
                return { type: 'select', value, label };
            }
            return { type: 'select', value: null, label: null };
        }
        case 'number':
            return {
                type: 'number',
                value: typeof value === 'number' ? value : null,
            };
        case 'boolean':
            return {
                type: 'boolean',
                value: typeof value === 'boolean' ? value : null,
            };
        case 'date':
        case 'timestamp':
        case 'text':
        case 'url':
        default:
            return {
                type: field.type,
                value: typeof value === 'string'
                    ? value
                    : value === null
                        ? null
                        : String(value),
            };
    }
}
/**
 * Resolve a raw entry value into normalized render data. Produces an `items`
 * array for `repeated` fields (one resolved entry per array element) and a
 * scalar `value` otherwise.
 */
function formatCMSFieldValue(field, raw) {
    if (field.type === 'group') {
        const subFields = (field.fields ?? []).filter((sub) => sub.status === 'active');
        const toObjectItem = (object) => {
            const record = object && typeof object === 'object' && !Array.isArray(object)
                ? object
                : {};
            const map = {};
            for (const sub of subFields) {
                map[sub.id] = formatCMSScalarValue(sub, record[sub.id]);
            }
            return map;
        };
        const rawItems = field.repeated
            ? Array.isArray(raw)
                ? raw
                : []
            : raw != null && !Array.isArray(raw)
                ? [raw]
                : [];
        return {
            type: 'group',
            value: null,
            objectItems: rawItems.map(toObjectItem),
        };
    }
    if (field.repeated) {
        const rawItems = Array.isArray(raw) ? raw : [];
        return {
            type: field.type,
            value: null,
            items: rawItems.map((item) => formatCMSScalarValue(field, item)),
        };
    }
    return formatCMSScalarValue(field, raw);
}
/**
 * Format an entry's active fields into normalized render data. Used by both the
 * publish pipeline (functions) and the dashboard editor preview so resolution
 * stays consistent.
 */
function formatCMSEntryForRender(collection, entry) {
    const fields = {};
    for (const field of collection.fields) {
        if (field.status !== 'active') {
            continue;
        }
        fields[field.id] = formatCMSFieldValue(field, entry.values[field.id]);
    }
    return { id: entry.id, fields };
}
// ============================================================================
// CMS binding attributes + dynamic-list query (shared functions/dashboard)
// ============================================================================
/**
 * CMS binding data-attribute names. MUST stay in sync with
 * apps/sites/src/utils/editable-parser-utils.ts (the isolated sites app keeps
 * its own copy).
 */
const CMS_ATTR = {
    collection: 'data-sf-cms-collection',
    entry: 'data-sf-cms-entry',
    field: 'data-sf-cms-field',
    fieldList: 'data-sf-cms-field-list',
    fieldItem: 'data-sf-cms-field-item',
    list: 'data-sf-list',
    listItem: 'data-sf-list-item',
    mode: 'data-sf-cms-mode',
    sort: 'data-sf-cms-sort',
    limit: 'data-sf-cms-limit',
    filter: 'data-sf-cms-filter',
};
/** Default data-freshness mode for a dynamic list when unspecified. */
const DEFAULT_CMS_LIST_MODE = 'snapshot';
function readCMSAttr(attribs, name) {
    const value = attribs?.[name];
    if (typeof value !== 'string') {
        return undefined;
    }
    const trimmed = value.trim();
    return trimmed === '' ? undefined : trimmed;
}
/** Data-freshness mode for a dynamic list. Defaults to snapshot. */
function parseCMSListMode(attribs) {
    return readCMSAttr(attribs, CMS_ATTR.mode) === 'live'
        ? 'live'
        : DEFAULT_CMS_LIST_MODE;
}
/** Parse `data-sf-cms-sort` of the form `{fieldId}:asc|desc`. */
function parseCMSSortAttr(attribs) {
    const raw = readCMSAttr(attribs, CMS_ATTR.sort);
    if (!raw) {
        return null;
    }
    const [fieldId, rawDirection] = raw.split(':');
    const trimmedFieldId = fieldId?.trim();
    if (!trimmedFieldId) {
        return null;
    }
    const direction = rawDirection?.trim().toLowerCase() === 'desc' ? 'desc' : 'asc';
    return { fieldId: trimmedFieldId, direction };
}
/** Parse `data-sf-cms-limit` as a positive integer. */
function parseCMSLimitAttr(attribs) {
    const raw = readCMSAttr(attribs, CMS_ATTR.limit);
    if (!raw) {
        return null;
    }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isInteger(parsed) || parsed <= 0) {
        return null;
    }
    return parsed;
}
/**
 * Parse `data-sf-cms-filter` (a JSON array of `{ fieldId, op, value }`).
 * Applied (AND across clauses) by `applyCMSListQuery` for snapshot/editor and
 * by `applyLiveListQuery` (sites) for live.
 */
function parseCMSFilterAttr(attribs) {
    const raw = readCMSAttr(attribs, CMS_ATTR.filter);
    if (!raw) {
        return null;
    }
    let parsed;
    try {
        parsed = JSON.parse(raw);
    }
    catch {
        return null;
    }
    const result = z.array(CMSFilterClauseSchema).safeParse(parsed);
    if (!result.success || result.data.length === 0) {
        return null;
    }
    return result.data;
}
/**
 * Parse a dynamic list's binding attributes into a typed query (sort + limit +
 * filter). Operates on a plain attribute record so it works with
 * htmlparser2 (functions) and DOMParser (dashboard) attribute maps. Mirrors
 * apps/sites/src/utils/cms-attribute-parser.ts.
 */
function parseCMSListQueryFromAttributes(attribs) {
    return {
        sort: parseCMSSortAttr(attribs),
        limit: parseCMSLimitAttr(attribs),
        filter: parseCMSFilterAttr(attribs),
    };
}
/**
 * Stable key identifying a dynamic list's resolved data, derived purely from
 * its collection + query. MUST stay in sync with `buildCmsListKey` in
 * apps/sites/src/utils/cms-attribute-parser.ts so publish/serve/editor agree.
 */
function buildCMSListKey(collectionId, query) {
    const sort = query.sort
        ? `${query.sort.fieldId}:${query.sort.direction}`
        : '';
    const limit = query.limit != null ? String(query.limit) : '';
    const filter = query.filter && query.filter.length ? JSON.stringify(query.filter) : '';
    return `${collectionId}::sort=${sort}::limit=${limit}::filter=${filter}`;
}
/** Normalize a raw field value into a comparable primitive for sorting. */
function toComparable(value) {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'number') {
        return value;
    }
    if (typeof value === 'boolean') {
        return value ? 1 : 0;
    }
    if (Array.isArray(value)) {
        return value.length;
    }
    if (typeof value === 'object' && typeof value.url === 'string') {
        return value.url;
    }
    return String(value);
}
function compareEntryValues(a, b) {
    const aEmpty = a === null || a === undefined;
    const bEmpty = b === null || b === undefined;
    if (aEmpty && bEmpty)
        return 0;
    if (aEmpty)
        return 1; // empties sort last
    if (bEmpty)
        return -1;
    const ca = toComparable(a);
    const cb = toComparable(b);
    if (typeof ca === 'number' && typeof cb === 'number') {
        return ca - cb;
    }
    const sa = String(ca);
    const sb = String(cb);
    return sa < sb ? -1 : sa > sb ? 1 : 0;
}
/** Coerce any value into a boolean for the `is` / boolean-equality operators. */
function toFilterBoolean(value) {
    if (typeof value === 'boolean')
        return value;
    if (typeof value === 'number')
        return value !== 0;
    if (typeof value === 'string')
        return value.trim().toLowerCase() === 'true';
    return false;
}
/**
 * Resolve a raw stored field value to a single comparable scalar. Repeated
 * (array) and group (object) values are not filterable in v1, and an asset
 * resolves to its url. Returns `null` for missing/unsupported values.
 */
function toFilterScalar(value) {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'string' ||
        typeof value === 'number' ||
        typeof value === 'boolean') {
        return value;
    }
    if (Array.isArray(value)) {
        return null;
    }
    if (typeof value === 'object' && typeof value.url === 'string') {
        return value.url;
    }
    return null;
}
/** Loose equality: numeric when both numeric, boolean-aware, else ci-string. */
function filterScalarEquals(scalar, operand) {
    if (operand === null ||
        operand === undefined ||
        Array.isArray(operand) ||
        typeof operand === 'object') {
        return false;
    }
    if (typeof scalar === 'number' && typeof operand === 'number') {
        return scalar === operand;
    }
    if (typeof scalar === 'boolean' || typeof operand === 'boolean') {
        return toFilterBoolean(scalar) === toFilterBoolean(operand);
    }
    return String(scalar).toLowerCase() === String(operand).toLowerCase();
}
/**
 * Ordered comparison for lt/lte/gt/gte/between. Number fields compare
 * numerically, date/timestamp by parsed time (lexical ISO fallback), and other
 * types numerically when both sides are numbers, else case-insensitive string.
 * Returns `null` when the operand is non-scalar or otherwise incomparable.
 */
function compareFilterScalars(field, scalar, operand) {
    if (operand === null ||
        operand === undefined ||
        Array.isArray(operand) ||
        typeof operand === 'object') {
        return null;
    }
    const type = field?.type;
    if (type === 'number') {
        const a = Number(scalar);
        const b = Number(operand);
        return Number.isNaN(a) || Number.isNaN(b) ? null : a - b;
    }
    if (type === 'date' || type === 'timestamp') {
        const a = Date.parse(String(scalar));
        const b = Date.parse(String(operand));
        if (Number.isNaN(a) || Number.isNaN(b)) {
            const sa = String(scalar);
            const sb = String(operand);
            return sa < sb ? -1 : sa > sb ? 1 : 0;
        }
        return a - b;
    }
    if (typeof scalar === 'number' && typeof operand === 'number') {
        return scalar - operand;
    }
    const sa = String(scalar).toLowerCase();
    const sb = String(operand).toLowerCase();
    return sa < sb ? -1 : sa > sb ? 1 : 0;
}
/** Whether a single raw field value satisfies one filter clause. */
function entryMatchesClause(field, raw, clause) {
    const scalar = toFilterScalar(raw);
    if (scalar === null) {
        // Missing / unsupported (repeated, group) values never match.
        return false;
    }
    const { op, value } = clause;
    switch (op) {
        case 'eq':
            return filterScalarEquals(scalar, value);
        case 'neq':
            return !filterScalarEquals(scalar, value);
        case 'contains':
            return (typeof scalar === 'string' &&
                String(value).length > 0 &&
                scalar.toLowerCase().includes(String(value).toLowerCase()));
        case 'is':
            return (toFilterBoolean(scalar) === toFilterBoolean(value));
        case 'in':
            return (Array.isArray(value) &&
                value.some((item) => filterScalarEquals(scalar, item)));
        case 'between': {
            if (!Array.isArray(value) || value.length < 2)
                return false;
            const lo = compareFilterScalars(field, scalar, value[0]);
            const hi = compareFilterScalars(field, scalar, value[1]);
            if (lo === null || hi === null)
                return false;
            return lo >= 0 && hi <= 0;
        }
        case 'lt':
        case 'lte':
        case 'gt':
        case 'gte': {
            if (Array.isArray(value))
                return false;
            const cmp = compareFilterScalars(field, scalar, value);
            if (cmp === null)
                return false;
            if (op === 'lt')
                return cmp < 0;
            if (op === 'lte')
                return cmp <= 0;
            if (op === 'gt')
                return cmp > 0;
            return cmp >= 0; // gte
        }
        default:
            return false;
    }
}
/**
 * Whether an entry satisfies ALL filter clauses (AND). Mirror of
 * `renderedEntryMatchesFilters` in apps/sites/src/utils/cms-live.ts — keep the
 * two in sync (sites is isolated and cannot import this).
 */
function cmsEntryMatchesFilters(collection, entry, filters) {
    return filters.every((clause) => {
        const field = collection.fields.find((f) => f.id === clause.fieldId);
        return entryMatchesClause(field, entry.values[clause.fieldId], clause);
    });
}
/**
 * Apply a dynamic list's query to a set of entries: filter (AND across
 * clauses), then sort (query sort, falling back to the collection's
 * defaultSort), then limit. Returns a new array; input is not mutated.
 */
function applyCMSListQuery(collection, entries, query) {
    const sort = query.sort ?? collection.defaultSort ?? null;
    let result = entries;
    if (query.filter && query.filter.length) {
        result = result.filter((entry) => cmsEntryMatchesFilters(collection, entry, query.filter));
    }
    if (sort) {
        result = [...result].sort((e1, e2) => {
            const cmp = compareEntryValues(e1.values[sort.fieldId], e2.values[sort.fieldId]);
            return sort.direction === 'desc' ? -cmp : cmp;
        });
    }
    if (query.limit != null) {
        result = result.slice(0, query.limit);
    }
    return result;
}

export { CMSAddEntriesToolInputSchema, CMSAssetReferenceSchema, CMSCollectionAttachmentSchema, CMSCollectionAttachmentSnapshotSchema, CMSCollectionFieldSchema, CMSCollectionSchema, CMSCollectionSourceSchema, CMSCollectionSubFieldSchema, CMSCollectionSyncStatusSchema, CMSCreateCollectionInputSchema, CMSCreateCollectionToolInputSchema, CMSCreateEntryInputSchema, CMSDefaultSortSchema, CMSDeleteCollectionToolInputSchema, CMSDeleteEntryToolInputSchema, CMSDuplicateCollectionToolInputSchema, CMSEditEntryToolInputSchema, CMSEntryAttachmentSchema, CMSEntryAttachmentSnapshotSchema, CMSEntryObjectValueSchema, CMSEntrySchema, CMSEntryStoredValueSchema, CMSEntryValueSchema, CMSFieldTypeSchema, CMSFilterClauseSchema, CMSFilterOpSchema, CMSListCollectionsToolInputSchema, CMSListQuerySchema, CMSPageEntriesToolInputSchema, CMSReadCollectionSchemaToolInputSchema, CMSRenderedAssetSchema, CMSRenderedFieldValueBaseSchema, CMSRenderedFieldValueSchema, CMSSelectOptionSchema, CMSSortDirectionSchema, CMSStatusSchema, CMSUpdateCollectionInputSchema, CMSUpdateCollectionToolInputSchema, CMSUpdateEntryInputSchema, CMS_ATTR, CachedCMSCollectionDataSchema, CachedCMSEntryDataSchema, DEFAULT_CMS_LIST_MODE, applyCMSListQuery, buildCMSListKey, formatCMSEntryForRender, parseCMSListMode, parseCMSListQueryFromAttributes, validateCMSEntryValues };
//# sourceMappingURL=content-library.esm.js.map
